/**
 * Par Seeker Style Leaderboard
 * Matches the official Par Seeker leaderboard design with tabs and clean table layout
 */
import React, { useState, useEffect, useRef } from 'react';
import { handicapLabel } from '../../../utils/handicap';
import { 
  Trophy, Medal, RefreshCw, Wifi, Star, Flag, Check, 
  ChevronDown, Award, Coins, TrendingUp, TrendingDown, Minus,
  MapPin, Clock, Users
} from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { useOutingApi } from '../hooks/useOutingApi';

// Score formatting helpers
const formatScore = (score) => {
  if (score === 0) return 'E';
  if (score > 0) return `+${score}`;
  return `${score}`;
};

const getScoreColor = (score) => {
  if (score < 0) return 'text-[#C9A227]'; // Gold for under par
  if (score === 0) return 'text-white';
  return 'text-red-400';
};

// Position badge styling
const getPositionStyle = (pos) => {
  if (pos === 1) return 'bg-[#C9A227] text-black font-bold';
  if (pos === 2) return 'bg-zinc-400 text-black font-bold';
  if (pos === 3) return 'bg-amber-700 text-white font-bold';
  return 'bg-[#1A1A1A] text-white';
};

// Trend indicator
const TrendIndicator = ({ trend }) => {
  if (!trend || trend === 0) return <Minus className="w-3 h-3 text-zinc-500" />;
  if (trend > 0) return <TrendingUp className="w-3 h-3 text-green-400" />;
  return <TrendingDown className="w-3 h-3 text-red-400" />;
};

// Player Avatar Component
const PlayerAvatar = ({ name, size = 'md' }) => {
  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??';
  const sizeClasses = {
    sm: 'w-6 h-6 text-[10px]',
    md: 'w-8 h-8 text-xs',
    lg: 'w-12 h-12 text-sm',
    xl: 'w-16 h-16 text-lg'
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-[#2A5A3B] to-[#1A3A2B] flex items-center justify-center text-white font-bold border-2 border-[#C9A227]/30`}>
      {initials}
    </div>
  );
};

// Status indicator
const StatusBadge = ({ status, holesCompleted }) => {
  if (holesCompleted >= 18) {
    return <span className="text-[10px] text-zinc-400 font-medium">F</span>;
  }
  return (
    <Star className="w-3 h-3 text-[#C9A227] fill-[#C9A227]" />
  );
};

// Podium for Gold Coins Tab
const GoldCoinPodium = ({ players }) => {
  if (!players || players.length === 0) return null;
  
  const topThree = players.slice(0, 3);
  const positions = [1, 0, 2]; // Display order: 2nd, 1st, 3rd
  
  return (
    <div className="flex items-end justify-center gap-2 py-6 px-4">
      {positions.map((posIndex) => {
        const player = topThree[posIndex];
        if (!player) return <div key={posIndex} className="w-24" />;
        
        const pos = posIndex + 1;
        const heights = { 1: 'pb-8', 2: 'pb-0', 3: 'pb-4' };
        const sizes = { 1: 'xl', 2: 'lg', 3: 'lg' };
        
        return (
          <div key={posIndex} className={`flex flex-col items-center ${heights[pos]}`}>
            {/* Wreath for 1st place */}
            {pos === 1 && (
              <div className="relative">
                <div className="absolute -inset-4 text-[#C9A227] text-4xl">🏆</div>
              </div>
            )}
            
            {/* Position number */}
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mb-2 ${
              pos === 1 ? 'bg-[#C9A227] text-black' :
              pos === 2 ? 'bg-zinc-400 text-black' :
              'bg-amber-700 text-white'
            }`}>
              {pos}
            </div>
            
            {/* Avatar */}
            <div className={`relative ${pos === 1 ? 'ring-4 ring-[#C9A227]/50 rounded-full' : ''}`}>
              <PlayerAvatar name={player.name} size={sizes[pos]} />
            </div>
            
            {/* Name */}
            <p className="text-white font-semibold text-xs mt-2 text-center truncate max-w-20">
              {player.name?.split(' ')[0]}
            </p>
            
            {/* Coins */}
            <div className="flex items-center gap-1 mt-1">
              <Coins className="w-4 h-4 text-[#C9A227]" />
              <span className="text-[#C9A227] font-bold text-sm">
                {(player.coins || 0).toLocaleString()}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Live Leaderboard Table Row
const LeaderboardRow = ({ team, position, isPlaying }) => {
  const thruDisplay = team.holes_completed >= 18 ? 'F' : team.holes_completed || '-';
  
  return (
    <div className={`flex items-center py-3 px-3 border-b border-white/5 ${
      position <= 3 ? 'bg-[#1A2A1F]' : 'hover:bg-white/5'
    }`}>
      {/* Position */}
      <div className="w-10 flex justify-center">
        <span className={`w-7 h-7 rounded flex items-center justify-center text-sm ${getPositionStyle(position)}`}>
          {position === 1 ? '🥇' : position === 2 ? '🥈' : position === 3 ? '🥉' : position}
        </span>
      </div>
      
      {/* Player/Team */}
      <div className="flex-1 flex items-center gap-3 ml-2">
        <PlayerAvatar name={team.team_name} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-sm truncate">{team.team_name}</span>
            {isPlaying && <Check className="w-3 h-3 text-[#C9A227]" />}
          </div>
          {team.players && team.players.length > 0 && (
            <p className="text-[10px] text-zinc-500 truncate">
              {team.players.map(p => p.name).join(', ')}
            </p>
          )}
        </div>
      </div>
      
      {/* Today Score */}
      <div className="w-14 text-center">
        <span className={`font-bold ${getScoreColor(team.to_par)}`}>
          {formatScore(team.to_par)}
        </span>
      </div>
      
      {/* Thru */}
      <div className="w-12 text-center flex items-center justify-center gap-1">
        <StatusBadge status={isPlaying ? 'playing' : 'waiting'} holesCompleted={team.holes_completed} />
        <span className="text-zinc-400 text-sm">{thruDisplay}</span>
      </div>
      
      {/* Total */}
      <div className="w-14 text-center">
        <span className={`font-bold ${getScoreColor(team.to_par)}`}>
          {formatScore(team.to_par)}
        </span>
      </div>
    </div>
  );
};

// Cut Line Component
const CutLine = ({ score }) => (
  <div className="flex items-center py-2 px-3 bg-red-900/20 border-y border-red-500/30">
    <div className="flex-1 border-t border-dashed border-red-500/50" />
    <span className="px-3 text-red-400 text-xs font-semibold">CUT LINE</span>
    <span className="text-red-400 text-xs font-bold">{formatScore(score)}</span>
    <div className="flex-1 border-t border-dashed border-red-500/50 ml-3" />
  </div>
);

// Rankings Table for World Rankings Tab
const RankingsTable = ({ players }) => (
  <div className="bg-[#0D0D0D] rounded-lg overflow-hidden">
    {/* Header */}
    <div className="flex items-center py-2 px-3 bg-[#1A1A1A] text-[10px] text-zinc-500 uppercase tracking-wider">
      <div className="w-10 text-center">Rank</div>
      <div className="flex-1 ml-2">Player</div>
      <div className="w-16 text-center">Location</div>
      <div className="w-12 text-center">HCP</div>
      <div className="w-10 text-center">Trend</div>
    </div>
    
    {/* Rows */}
    {players.map((player, i) => (
      <div key={i} className="flex items-center py-3 px-3 border-b border-white/5 hover:bg-white/5">
        <div className="w-10 text-center">
          <span className={`font-bold ${i < 3 ? 'text-[#C9A227]' : 'text-white'}`}>{i + 1}</span>
        </div>
        <div className="flex-1 flex items-center gap-3 ml-2">
          <PlayerAvatar name={player.name} size="sm" />
          <div>
            <p className="text-white text-sm font-medium">{player.name}</p>
            <p className="text-[10px] text-zinc-500">{player.course || 'Par Seeker'}</p>
          </div>
        </div>
        <div className="w-16 text-center text-zinc-400 text-xs">{player.location || '-'}</div>
        <div data-testid={`outing-player-handicap-${player.player_id || player.id}`} className="w-24 text-center text-white text-xs font-medium">{handicapLabel(player.handicap)}</div>
        <div className="w-10 flex justify-center">
          <TrendIndicator trend={player.trend} />
        </div>
      </div>
    ))}
  </div>
);

// Main Leaderboard Component
export default function ParSeekerLeaderboard({ outingId, refreshInterval = 15000 }) {
  const api = useOutingApi();
  const [activeTab, setActiveTab] = useState('live');
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const prevLeaderboardRef = useRef(null);

  const fetchLeaderboard = async () => {
    try {
      const data = await api.getLeaderboard(outingId);
      if (data.success) {
        // Calculate position changes
        if (prevLeaderboardRef.current) {
          const prevPositions = {};
          prevLeaderboardRef.current.teams?.forEach((t, i) => {
            prevPositions[t.team_id] = i + 1;
          });
          
          data.teams = data.teams?.map((team, i) => ({
            ...team,
            position_change: prevPositions[team.team_id] 
              ? prevPositions[team.team_id] - (i + 1)
              : 0
          }));
        }
        
        prevLeaderboardRef.current = data;
        setLeaderboard(data);
        setLastUpdate(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, refreshInterval);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outingId, refreshInterval]);

  const tabs = [
    { id: 'rankings', label: 'RANKINGS' },
    { id: 'live', label: 'LIVE' },
    { id: 'gold_coins', label: 'GOLD COINS' }
  ];

  const teams = leaderboard?.teams || [];

  // Mock data for other tabs (would come from API in production)
  const mockRankings = teams.map((t, i) => ({
    name: t.team_name,
    location: 'Local',
    handicap: Math.floor(Math.random() * 20) + 5,
    trend: Math.random() > 0.5 ? 1 : Math.random() > 0.5 ? -1 : 0
  }));

  const mockGoldCoins = teams.map((t, i) => ({
    name: t.team_name,
    coins: Math.floor(Math.random() * 200000) + 50000,
    coinsWon: Math.floor(Math.random() * 50000),
    gamesWon: Math.floor(Math.random() * 50),
    streak: Math.floor(Math.random() * 10)
  })).sort((a, b) => b.coins - a.coins);

  return (
    <div className="bg-[#0A0A0A] min-h-full" data-testid="parseeker-leaderboard">
      {/* Tabs */}
      <div className="flex border-b border-[#C9A227]/30">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`leaderboard-tab-${tab.id}`}
            className={`flex-1 py-3 text-sm font-semibold transition-colors ${
              activeTab === tab.id 
                ? 'text-[#C9A227] border-b-2 border-[#C9A227]' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Live Tab Content */}
      {activeTab === 'live' && (
        <div>
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h2 className="text-white font-bold text-lg">LIVE LEADERBOARD</h2>
                <Badge className="bg-red-500 text-white text-[10px] px-2 animate-pulse">LIVE</Badge>
              </div>
              <Button variant="ghost" size="sm" className="text-zinc-400 gap-1 text-xs">
                ROUND 1 <ChevronDown className="w-3 h-3" />
              </Button>
            </div>
            
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1 text-zinc-400">
                <MapPin className="w-3 h-3" />
                <span>{leaderboard?.course_name || 'Course TBD'}</span>
              </div>
              <div className="flex items-center gap-1 text-zinc-500">
                <Clock className="w-3 h-3" />
                <span>Last updated: {lastUpdate ? 'Just now' : '-'}</span>
              </div>
            </div>
          </div>

          {/* Table Header */}
          <div className="flex items-center py-2 px-3 bg-[#1A1A1A] text-[10px] text-zinc-500 uppercase tracking-wider sticky top-0 z-10">
            <div className="w-10 text-center">POS</div>
            <div className="flex-1 ml-2">PLAYER</div>
            <div className="w-14 text-center">TODAY</div>
            <div className="w-12 text-center">THRU</div>
            <div className="w-14 text-center">TOTAL</div>
          </div>

          {/* Leaderboard Rows */}
          {teams.length > 0 ? (
            <div>
              {teams.map((team, i) => (
                <LeaderboardRow 
                  key={team.team_id} 
                  team={team} 
                  position={i + 1}
                  isPlaying={team.holes_completed > 0 && team.holes_completed < 18}
                />
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-white font-bold mb-2">Waiting for Teams</h3>
              <p className="text-zinc-500 text-sm">
                The leaderboard will update automatically as teams submit scores.
              </p>
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 py-4 border-t border-white/10 text-[10px]">
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-[#C9A227] fill-[#C9A227]" />
              <span className="text-zinc-400">On Course</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-zinc-400">Playing</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-zinc-400 font-bold">F</span>
              <span className="text-zinc-400">= Finished</span>
            </div>
          </div>
        </div>
      )}

      {/* Rankings Tab Content */}
      {activeTab === 'rankings' && (
        <div>
          {/* Header Card */}
          <div className="p-4 bg-gradient-to-r from-[#1A2A1F] to-[#0D0D0D] border-b border-[#C9A227]/20">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-[#C9A227]" />
              <h2 className="text-white font-bold">PAR SEEKER WORLD RANKING</h2>
              <Badge className="bg-[#1A1A1A] text-[#C9A227] text-[10px]">2025 SEASON</Badge>
            </div>
            <p className="text-zinc-400 text-xs">The official ranking of Par Seeker players.</p>
            
            {/* Filter Pills */}
            <div className="flex gap-2 mt-4 overflow-x-auto">
              {['WORLD', 'STATE', 'LOCAL', 'COURSE', 'FRIENDS'].map(filter => (
                <button
                  key={filter}
                  className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                    filter === 'WORLD' 
                      ? 'bg-[#C9A227] text-black' 
                      : 'bg-[#1A1A1A] text-zinc-400 hover:text-white'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* Rankings Table */}
          <div className="p-4">
            {mockRankings.length > 0 ? (
              <RankingsTable players={mockRankings} />
            ) : (
              <div className="p-8 text-center">
                <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-white font-bold mb-2">No Rankings Yet</h3>
                <p className="text-zinc-500 text-sm">Complete rounds to appear in rankings.</p>
              </div>
            )}
          </div>

          {/* How Rankings Work */}
          <div className="mx-4 mb-4 p-4 bg-[#1A2A1F] rounded-lg border border-[#C9A227]/20">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-[#C9A227]" />
              <h3 className="text-white font-semibold text-sm">HOW RANKINGS WORK</h3>
            </div>
            <p className="text-zinc-400 text-xs">
              Earn ranking points by playing in events, finishing in the top 10 and winning tournaments.
            </p>
            <Button variant="outline" size="sm" className="mt-3 text-xs border-[#C9A227] text-[#C9A227]">
              LEARN MORE
            </Button>
          </div>
        </div>
      )}

      {/* Gold Coins Tab Content */}
      {activeTab === 'gold_coins' && (
        <div>
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <Coins className="w-5 h-5 text-[#C9A227]" />
              <h2 className="text-white font-bold">GOLD COIN LEADERBOARD</h2>
            </div>
            <p className="text-zinc-400 text-xs">Compete. Earn. Climb the board.</p>
            
            {/* Filter Pills */}
            <div className="flex gap-2 mt-4">
              {['ALL TIME', 'GLOBAL', 'STATE', 'COURSE', 'FRIENDS'].map((filter, i) => (
                <button
                  key={filter}
                  className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                    i === 0 
                      ? 'bg-[#C9A227] text-black' 
                      : 'bg-[#1A1A1A] text-zinc-400 hover:text-white'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* Podium */}
          {mockGoldCoins.length >= 3 && (
            <GoldCoinPodium players={mockGoldCoins} />
          )}

          {/* Table */}
          <div className="bg-[#0D0D0D] mx-4 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center py-2 px-3 bg-[#1A1A1A] text-[10px] text-zinc-500 uppercase tracking-wider">
              <div className="w-8 text-center">Rank</div>
              <div className="flex-1 ml-2">Player</div>
              <div className="w-16 text-center">Coin Balance</div>
              <div className="w-14 text-center">Coins Won</div>
              <div className="w-12 text-center">Games Won</div>
              <div className="w-10 text-center">Streak</div>
            </div>
            
            {/* Rows */}
            {mockGoldCoins.slice(0, 10).map((player, i) => (
              <div key={i} className="flex items-center py-3 px-3 border-b border-white/5 hover:bg-white/5">
                <div className="w-8 text-center">
                  <span className={`font-bold ${i < 3 ? 'text-[#C9A227]' : 'text-white'}`}>{i + 1}</span>
                </div>
                <div className="flex-1 flex items-center gap-2 ml-2">
                  <PlayerAvatar name={player.name} size="sm" />
                  <span className="text-white text-sm font-medium truncate">{player.name}</span>
                </div>
                <div className="w-16 text-center">
                  <span className="text-[#C9A227] font-bold text-xs">{player.coins?.toLocaleString()}</span>
                </div>
                <div className="w-14 text-center text-zinc-400 text-xs">{player.coinsWon?.toLocaleString()}</div>
                <div className="w-12 text-center text-zinc-400 text-xs">{player.gamesWon}</div>
                <div className="w-10 text-center text-green-400 text-xs">{player.streak}</div>
              </div>
            ))}
          </div>

          {/* Earn More Banner */}
          <div className="m-4 p-4 bg-gradient-to-r from-[#1A2A1F] to-[#2A3A2F] rounded-lg border border-[#C9A227]/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold text-sm flex items-center gap-2">
                  <Coins className="w-4 h-4 text-[#C9A227]" />
                  EARN MORE GOLD COINS
                </h3>
                <p className="text-zinc-400 text-xs mt-1">
                  Join events, win games, place in the top 10 and complete challenges.
                </p>
              </div>
              <Button size="sm" className="bg-[#C9A227] hover:bg-[#B8922A] text-black font-bold text-xs">
                PLAY & EARN
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
